import { useCallback } from 'react'

import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { libraryAgencyQueryKeys } from '@/hooks/library/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginatedResponse } from '@/types/Api'
import type { UUID } from '@/types/Id'
import type { EntityAgency } from '@/types/library/Entity'

interface UseAgenciesArgs<T> {
  orgId: UUID | ''
  withConfirmed?: boolean
  withSuggested?: boolean
  select?: (data: PaginatedResponse<EntityAgency>) => T
  enabled?: boolean
}

interface UseAgencyArgs<T> {
  orgId: UUID | ''
  agencyId: UUID | ''
  select?: (data: EntityAgency) => T
}

const buildAgenciesUrl = (
  orgId: UUID,
  options: { withConfirmed?: boolean; withSuggested?: boolean } = {},
) => {
  const url = new URL(`${API_URL}/api/v1/organizations/${orgId}/entities`)
  url.searchParams.append('entity_types', 'agency')
  if (typeof options.withConfirmed === 'boolean') {
    url.searchParams.append('with_confirmed', String(options.withConfirmed))
  }
  if (typeof options.withSuggested === 'boolean') {
    url.searchParams.append('with_suggested', String(options.withSuggested))
  }
  return url
}

export const fetchAgencies = async (
  orgId: UUID | '',
  accessToken: string,
  options: { withConfirmed?: boolean; withSuggested?: boolean } = {},
): Promise<PaginatedResponse<EntityAgency>> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }

  const url = buildAgenciesUrl(orgId as UUID, options)
  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  })
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as PaginatedResponse<EntityAgency>
}

const fetchAgency = async (
  orgId: UUID | '',
  agencyId: UUID | '',
  accessToken: string,
): Promise<EntityAgency> => {
  if (!orgId) {
    throw new Error('orgId is required')
  }
  if (!agencyId) {
    throw new Error('agencyId is required')
  }

  const response = await fetch(
    `${API_URL}/api/v1/organizations/${orgId}/entities/${agencyId}`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    },
  )
  if (!response.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: response.statusText,
      cause: response,
    })
  }
  return (await response.json()) as EntityAgency
}

export const useAgencies = <T = EntityAgency[]>({
  orgId,
  withConfirmed,
  withSuggested,
  select,
  enabled = true,
}: UseAgenciesArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: PaginatedResponse<EntityAgency>) => {
      if (select) {
        return select(data)
      }
      return data.data
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)) as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: libraryAgencyQueryKeys.organization({
      orgId,
      withConfirmed,
      withSuggested,
    }),
    queryFn: () =>
      fetchAgencies(orgId, accessToken, { withConfirmed, withSuggested }),
    enabled: enabled && !!orgId && !!accessToken,
    staleTime: Infinity,
    placeholderData: (previousData) => previousData,
    select: selectFn,
  })
}

export const useAgency = <T = EntityAgency>({
  orgId,
  agencyId,
  select,
}: UseAgencyArgs<T>) => {
  const { accessToken } = useAuthInfoWithThrow()

  const selectFn = useCallback(
    (data: EntityAgency) => {
      if (select) {
        return select(data)
      }
      return data as unknown as T
    },
    [select],
  )

  return useQuery({
    queryKey: libraryAgencyQueryKeys.agency({ orgId, agencyId }),
    queryFn: () => fetchAgency(orgId, agencyId, accessToken),
    enabled: !!orgId && !!agencyId && !!accessToken,
    staleTime: Infinity,
    select: selectFn,
  })
}
